import { AuthorizationRequirement } from '../models/authorization-requirement';
import { User, UserRole } from '../models/user';
import { UserPermissionScope } from '../models/user-permissions';

export const USER_ROLE_PRIORITY: Record<UserRole, number> = {
  [UserRole.ADMIN]: 0,
  [UserRole.MANAGER]: 1,
  [UserRole.WORKFLOW_EDITOR]: 2,
  [UserRole.JOB_MANAGER]: 3,
  [UserRole.JOB_UPLOADER]: 4,
  [UserRole.WORKER_MANAGER]: 5,
  [UserRole.USER]: 6,
};

export function normalizeUserRoles(
  roles: UserRole | string | Array<UserRole | string | null | undefined> | null | undefined,
): UserRole[] {
  const normalized = new Set<UserRole>([UserRole.USER]);
  const rawRoles = Array.isArray(roles) ? roles : [roles];

  rawRoles.forEach((role) => {
    if (!role) {
      return;
    }

    const normalizedRole = typeof role === 'string'
      ? UserRole[role.toUpperCase() as keyof typeof UserRole]
      : role;

    if (normalizedRole) {
      normalized.add(normalizedRole);
    }
  });

  return Array.from(normalized).sort(
    (left, right) => USER_ROLE_PRIORITY[left] - USER_ROLE_PRIORITY[right],
  );
}

export function derivePrimaryRole(
  roles: UserRole | string | Array<UserRole | string | null | undefined> | null | undefined,
): UserRole {
  return normalizeUserRoles(roles)[0] ?? UserRole.USER;
}

export function serializeUserRolesForRequest(
  roles: UserRole | string | Array<UserRole | string | null | undefined> | null | undefined,
): UserRole[] {
  return normalizeUserRoles(roles).filter((role) => role !== UserRole.USER);
}

export function getAssignableUserRolesForEditor(
  editorRole: UserRole | string | null | undefined,
): UserRole[] {
  switch (editorRole) {
    case UserRole.ADMIN:
      return Object.values(UserRole).filter((role) => role !== UserRole.USER);
    case UserRole.MANAGER:
      return [
        UserRole.MANAGER,
        UserRole.WORKFLOW_EDITOR,
        UserRole.JOB_MANAGER,
        UserRole.JOB_UPLOADER,
        UserRole.WORKER_MANAGER,
      ];
    default:
      return [UserRole.MANAGER];
  }
}

export function hasRole(user: User | null | undefined, role: UserRole): boolean {
  return !!user && normalizeUserRoles(user.roles).includes(role);
}

export function hasAnyRole(
  user: User | null | undefined,
  roles: UserRole[] | null | undefined,
): boolean {
  if (!roles?.length) {
    return true;
  }

  return roles.some((role) => hasRole(user, role));
}

export function hasPermission(
  user: User | null | undefined,
  permission: string,
  organizationId?: string | null,
): boolean {
  if (!user) {
    return false;
  }

  if (user.hasAllPermissions) {
    return true;
  }

  if (!user.permissions?.includes(permission)) {
    return false;
  }

  const matchingScopes = (user.permissionScopes ?? []).filter(
    (scope) => scope.permission === permission,
  );

  if (matchingScopes.length === 0) {
    return true;
  }

  if (!organizationId) {
    return matchingScopes.length > 0;
  }

  return matchingScopes.some((scope) => scope.organizationIds.includes(organizationId));
}

export function hasAnyPermission(
  user: User | null | undefined,
  permissions: string[] | null | undefined,
  organizationId?: string | null,
): boolean {
  if (!permissions?.length) {
    return true;
  }

  return permissions.some((permission) => hasPermission(user, permission, organizationId));
}

export function hasAllPermissions(
  user: User | null | undefined,
  permissions: string[] | null | undefined,
  organizationId?: string | null,
): boolean {
  if (!permissions?.length) {
    return true;
  }

  return permissions.every((permission) => hasPermission(user, permission, organizationId));
}

export function matchesAuthorizationRequirement(
  user: User | null | undefined,
  requirement?: AuthorizationRequirement | null,
  organizationId?: string | null,
): boolean {
  if (!requirement) {
    return true;
  }

  const roleMatched = requirement.anyRoles?.length
    ? hasAnyRole(user, requirement.anyRoles)
    : false;
  const permissionMatched = requirement.anyPermissions?.length
    ? hasAnyPermission(user, requirement.anyPermissions, organizationId)
    : false;
  const roleOrPermissionMatched = requirement.anyRoles?.length || requirement.anyPermissions?.length
    ? roleMatched || permissionMatched
    : true;

  return roleOrPermissionMatched
    && hasAllPermissions(user, requirement.allPermissions, organizationId);
}

export function mergePermissionScopes(
  scopes: UserPermissionScope[] | null | undefined,
): UserPermissionScope[] {
  return scopes?.map((scope) => ({
    ...scope,
    organizationIds: Array.from(new Set(scope.organizationIds ?? [])),
  })) ?? [];
}
